import '../reviewBooking/reviewBooking.css';
import '../reviewBookingOthers/reviewBookSection/reviewBookInfo/reviewBookInfo.css';
import '../reviewBookingOthers/reviewBookSection/reviewBookSumm/reviewBookSumm.css';
import '../tripDetails/tripBookingTab/tripBookingTab.css';
import { useContext, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';
import TitleBar from '../../components/titleBar/titleBar';
import Payment from '../../components/payment/payment';
import { UserAuthContext } from '../../context/UserAuthContext';
import { UserLoginSecContext } from '../../context/UserLoginSecContext';
import { userInfoFun } from '../../api/userAPI';
import { bookingStatus, stayReserveFun } from '../../api/bookingAPI';
import errorHandle from '../../utils/errorHandle';
import { campStayQuery, unitsQuery } from '../../utils/campStay';
import StaySummary from './components/StaySummary';
import LeadGuestForm from './components/LeadGuestForm';
import StayPricePanel from './components/StayPricePanel';
import { readStayBooking } from './campReviewFun/stayBookingStore';
import { stayAmounts } from './campReviewFun/stayAmounts';
import validateLeadGuest from './campReviewFun/validateLeadGuest';

const TYPE = 'camp';
// same wait as the other review pages before sending the guest to "verifying payment"
const PAYMENT_WAIT_MS = 45000;

// "Change Details": dates, guests, the units picked and the reserve choice
const changeDetailsQuery = (stay) => {
    const params = new URLSearchParams(
        campStayQuery({ checkin: stay.checkIn, checkout: stay.checkOut, ...stay.guests })
    );
    const items = stay.items?.length ? stay.items : [{ unitId: stay.unitId, rooms: stay.rooms }];
    const units = unitsQuery(items);
    if (units) params.set('units', units);
    if (stay.isReserved) params.set('reserve', '1');
    return params.toString();
};

/**
 * /camp/review-booking — lead guest details + payment for a stay.
 * The stay comes from CampBookingTab ("Book Now"), via navigation state or
 * sessionStorage (so a refresh keeps it).
 */
export default function ReviewBookingCamp() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useContext(UserAuthContext);
    const { setLogButton, setLoginSec } = useContext(UserLoginSecContext);
    const loggedIn = Boolean(isAuthenticated);

    const [stay] = useState(() => state?.stay || readStayBooking());
    const [guestMode, setGuestMode] = useState(false);
    const [leadGuest, setLeadGuest] = useState({ name: '', emailId: '', phoneNumber: '', countryCode: '+91' });
    const [specialRequests, setSpecialRequests] = useState('');
    const [paymentType, setPaymentType] = useState('UPI');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [order, setOrder] = useState(null); // { orderId, bookingId, amount }
    const paymentRef = useRef();

    // logged in: fill the form from the account
    useEffect(() => {
        if (!loggedIn) return;
        userInfoFun()
            .then(({ data }) =>
                setLeadGuest((g) => ({
                    ...g,
                    name: g.name || data?.name || '',
                    emailId: g.emailId || data?.emailId || '',
                    phoneNumber: g.phoneNumber || String(data?.phoneNumber || ''),
                    countryCode: data?.countryCode || g.countryCode
                }))
            )
            .catch(() => {});
    }, [loggedIn]);

    // came back after paying in another tab -> go to the confirmation
    useEffect(() => {
        const booked = JSON.parse(localStorage.getItem('bookedData') || 'null');
        if (!booked?.bookingId || localStorage.getItem('bookingType') !== TYPE) return;
        bookingStatus(TYPE, booked.bookingId)
            .then((res) => {
                if (res?.data?.paymentInfo?.status === 'Paid') {
                    localStorage.setItem('backendBookingData', JSON.stringify({ ...res.data, type: TYPE }));
                    navigate('/booking-confirmation');
                }
            })
            .catch(() => {});
    }, [navigate]);

    // order created -> open Razorpay
    useEffect(() => {
        if (!order?.orderId) return undefined;
        paymentRef.current?.initiatePayment();
        window.paymentTimeout = setTimeout(() => {
            if (window.location.pathname !== '/booking-confirmation') {
                navigate('/payment-verification', { state: { type: TYPE } });
            }
        }, PAYMENT_WAIT_MS);
        return undefined;
    }, [order, navigate]);

    if (!stay?.listingId) return <Navigate to='/' replace />;

    const canFillDetails = loggedIn || guestMode;
    const { payNow } = stayAmounts(stay, paymentType);

    const confirm = async () => {
        if (!canFillDetails) {
            toast.info('Please login or continue as a guest');
            return;
        }
        const problem = validateLeadGuest(leadGuest);
        if (problem) {
            toast.error(problem);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                listingId: stay.listingId,
                // every unit selected (older saved stays have one unitId + rooms)
                items: stay.items?.length
                    ? stay.items.map(({ unitId, rooms }) => ({ unitId, rooms }))
                    : [{ unitId: stay.unitId, rooms: stay.rooms }],
                unitId: stay.unitId,
                checkIn: stay.checkIn,
                checkOut: stay.checkOut,
                adults: stay.guests.adults,
                children: stay.guests.children,
                pets: stay.guests.pets,
                rooms: stay.rooms,
                isReserved: Boolean(stay.isReserved),
                paymentType,
                leadGuest: { ...leadGuest, name: leadGuest.name.trim() },
                specialRequests: specialRequests.trim()
            };
            const { data } = await stayReserveFun(payload, { guest: !loggedIn });

            // same keys the payment verification / confirmation pages read
            localStorage.setItem('bookedData', JSON.stringify(data));
            localStorage.setItem(
                'bookingData1',
                JSON.stringify({
                    ...stay,
                    packId: stay.listingId,
                    paymentType,
                    amountPaid: { paid: data.amount }
                })
            );
            localStorage.setItem('bookingType', TYPE);
            localStorage.setItem('paymentType', paymentType);
            localStorage.setItem('tripImage', stay.image || '');
            localStorage.setItem('packId', stay.listingId);

            setOrder(data);
        } catch (error) {
            // e.g. "Someone just booked the last room" from the server
            const message = error?.response?.data?.message;
            if (message) toast.error(message);
            else errorHandle(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            {loading && (
                <div className={verifying ? 'loadingBackgroundPayment' : 'loadingBackground'}>
                    <ReactLoading type='spin' color='#006aff' className={verifying ? 'loadingSpinPayment' : 'loadingSpin'} />
                    {verifying && <p className='loadingText'>Please wait while we verify your payment…</p>}
                </div>
            )}

            <TitleBar title='Review Booking Details' />

            <div className='bookingReviewDetails'>
                <div className='reviewBoInfoDiv'>
                    <StaySummary stay={stay} />

                    {!loggedIn && (
                        <p className='reviewBoInfoNoteP'>
                            Note - If you are continuing as guest, The lead guest details will be used for
                            communication for this booking.
                        </p>
                    )}

                    {!canFillDetails && (
                        <div className='reviewBoInfoBtnDivLog'>
                            <button
                                type='button'
                                className='reviewBoInfoLoginBtn'
                                onClick={() => {
                                    setLoginSec('login');
                                    setLogButton(true);
                                }}
                            >
                                Login
                            </button>
                            <div className='reviewBoInfoBtnGueDiv'>
                                <p>Or Continue as a</p>
                                <button
                                    type='button'
                                    className='reviewBoInfoGuestBtn grayBtn'
                                    onClick={() => setGuestMode(true)}
                                >
                                    Guest
                                </button>
                            </div>
                        </div>
                    )}

                    {!loggedIn && guestMode && <p className='reviewBoInfoNoteP'>Continuing as guest.</p>}

                    {canFillDetails && (
                        <LeadGuestForm
                            guest={leadGuest}
                            onChange={setLeadGuest}
                            specialRequests={specialRequests}
                            onRequestsChange={setSpecialRequests}
                        />
                    )}
                </div>

                <div className='bookingReviewPriceDiv'>
                    <StayPricePanel
                        stay={stay}
                        paymentType={paymentType}
                        onPaymentTypeChange={setPaymentType}
                        onConfirm={confirm}
                        onChangeDetails={() =>
                            // back to the stay with the same dates, guests and units filled in
                            navigate(`/camp/details/${stay.listingId}?${changeDetailsQuery(stay)}`)
                        }
                        disabled={loading}
                    />
                </div>
            </div>

            <Payment
                ref={paymentRef}
                type={TYPE}
                paymentType={paymentType}
                paymentData={{ orderId: order?.orderId, bookingId: order?.bookingId }}
                options={{
                    amount: Math.round((order?.amount ?? payNow) * 100),
                    name: stay.title,
                    description: `${stay.unitName || 'Stay'} · ${stay.checkIn} to ${stay.checkOut}`,
                    prefill: {
                        name: leadGuest.name,
                        email: leadGuest.emailId,
                        contact: leadGuest.phoneNumber
                    }
                }}
                setLoading={(value) => {
                    setVerifying(Boolean(value));
                    setLoading(value);
                }}
                setChangeLoading={setVerifying}
            />
        </div>
    );
}
